import { useQuery } from '@apollo/client';
import { Bell, BellRing, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { playNotificationChime } from './notification-sound';
import { NOTIFICATIONS_QUERY } from './notifications.gql';

import {
  NOTIFICATION_AUDIENCE_FROM_GQL,
  NOTIFICATION_AUDIENCE_TO_GQL,
  NOTIFICATION_KIND_FROM_GQL,
} from '@/shared/graphql/enums';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { useAuthStore } from '@/stores/authStore';
import {
  type Notification,
  type NotificationAudience,
  useNotificationsStore,
} from '@/stores/notificationsStore';

interface RawNotification {
  id: string;
  kind: string;
  branch_id: string | null;
  audience: string[];
  title_key: string;
  body_key: string;
  body_args_json: string;
  reference_kind: string | null;
  reference_id: string | null;
  amount_cents: string | null;
  created_at: string;
}

interface NotificationsResponse {
  notifications: { items: RawNotification[]; latest_id: string | null };
}

const SOUND_KINDS = new Set<Notification['kind']>([
  'order_sent_to_cashier',
  'order_returned_to_doctor',
]);

function audienceForRoles(roles: readonly string[]): NotificationAudience | null {
  if (roles.includes('administrator')) return 'admin';
  if (roles.includes('cashier')) return 'cashier';
  if (roles.includes('veterinarian')) return 'doctor';
  return null;
}

function parseArgs(s: string): Record<string, string | number | null> {
  if (!s) return {};
  try {
    const v = JSON.parse(s) as unknown;
    if (v && typeof v === 'object') return v as Record<string, string | number | null>;
  } catch {
    /* swallow */
  }
  return {};
}

function isNotificationAudience(value: NotificationAudience | undefined): value is NotificationAudience {
  return value !== undefined;
}

export function NotificationsBell(): JSX.Element | null {
  const { t } = useTranslation();
  const roles = useAuthStore((s) => s.user?.roles ?? []);
  const audience = useMemo(() => audienceForRoles(roles), [roles]);
  const items = useNotificationsStore((s) => s.items);
  const ingest = useNotificationsStore((s) => s.ingest);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const latest_seen_id = useNotificationsStore((s) => s.latest_seen_id);
  const setLatestSeenId = useNotificationsStore((s) => s.setLatestSeenId);
  const [open, setOpen] = useState(false);
  const gql_audience = audience ? NOTIFICATION_AUDIENCE_TO_GQL[audience] : null;
  const audience_ref = useRef(audience);
  audience_ref.current = audience;

  const { data } = useQuery<NotificationsResponse>(NOTIFICATIONS_QUERY, {
    variables: { audience: gql_audience, after_id: latest_seen_id, limit: 50 },
    pollInterval: 15_000,
    fetchPolicy: 'network-only',
    skip: audience === null,
  });

  useEffect(() => {
    const incoming = data?.notifications.items ?? [];
    if (incoming.length === 0) {
      const lid = data?.notifications.latest_id;
      if (lid && lid !== latest_seen_id) setLatestSeenId(lid);
      return;
    }
    const parsed = incoming.flatMap((n) => {
      const kind = NOTIFICATION_KIND_FROM_GQL[n.kind as keyof typeof NOTIFICATION_KIND_FROM_GQL];
      if (!kind) return [];
      return [
        {
          id: n.id,
          kind,
          branch_id: n.branch_id,
          audience: n.audience
            .map((item) => NOTIFICATION_AUDIENCE_FROM_GQL[item as keyof typeof NOTIFICATION_AUDIENCE_FROM_GQL])
            .filter(isNotificationAudience),
          title_key: n.title_key,
          body_key: n.body_key,
          body_args: parseArgs(n.body_args_json),
          reference_kind: n.reference_kind,
          reference_id: n.reference_id,
          amount_cents: n.amount_cents,
          created_at: n.created_at,
        },
      ];
    });
    const fresh = ingest(parsed);
    const lid = data?.notifications.latest_id ?? parsed[0]?.id ?? null;
    if (lid) setLatestSeenId(lid);
    if (fresh.length > 0 && audience_ref.current === 'cashier') {
      const chime = fresh.some((n) => SOUND_KINDS.has(n.kind));
      if (chime) playNotificationChime();
    }
  }, [data, ingest, setLatestSeenId, latest_seen_id]);

  if (audience === null) return null;

  const unread_count = items.filter((it) => !it.read).length;

  const onClickToggle = (): void => {
    setOpen((prev) => {
      const next = !prev;
      if (next) markAllRead();
      return next;
    });
  };

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClickToggle}
        aria-label={t('notifications.bell_aria')}
      >
        {unread_count > 0 ? (
          <BellRing className="h-4 w-4 text-primary" aria-hidden />
        ) : (
          <Bell className="h-4 w-4" aria-hidden />
        )}
        {unread_count > 0 ? (
          <span className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
            {unread_count > 99 ? '99+' : unread_count}
          </span>
        ) : null}
      </Button>
      {open ? (
        <div className="absolute right-0 z-40 mt-2 w-[22rem] max-w-[calc(100vw-2rem)]">
          <Card className="shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
              <CardTitle className="text-sm">{t('notifications.title')}</CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                aria-label={t('common.close')}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </CardHeader>
            <CardContent className="max-h-[60vh] space-y-2 overflow-y-auto p-3">
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('notifications.empty')}</p>
              ) : (
                items.slice(0, 30).map((n) => (
                  <div
                    key={n.id}
                    className="rounded-md border border-border bg-card/50 p-2 text-xs"
                  >
                    <p className="text-sm font-medium">
                      {String(t(n.title_key, n.body_args as Record<string, string>))}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {String(t(n.body_key, n.body_args as Record<string, string>))}
                    </p>
                    <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </CardContent>
            <CardFooter className="justify-end gap-2 p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => useNotificationsStore.getState().clear()}
              >
                {t('notifications.clear')}
              </Button>
            </CardFooter>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
